import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, RotateCcw } from 'lucide-react';
import { ExecStatusPill } from './ExecStatusPill';
import { ApprovalChain } from './ApprovalChain';
import { fetchExecutionPlaybooks, ExecutionPlaybooksError } from '../services/executionPlaybooksService';
import { fetchCommandCenter, CommandCenterError } from '../services/commandCenterService';
import { mapCommandCenter, type InitiativeStatus } from '../utils/mapCommandCenter';
import { approvalStepsFromProgress, type ApprovalStep } from '../utils/executeChrome';
import { formatEstimatedValueRangeM } from '../utils/formatFinancialDisplay';
import { useEA } from '../contexts/EAContext';
import {
  formatExecutionPostureLabel,
  shortlistPostureLabel,
} from '../utils/executionPosture';

export interface PortfolioCardModel {
  id: string;
  title: string;
  status: InitiativeStatus;
  sponsor: string;
  owner: string;
  phase: string;
  roi: string;
  approval: ApprovalStep[];
  milestonesDone: number;
  milestonesTotal: number;
  nextPhase: string;
  /** True while Command Center sponsor/owner/approval/milestones are still loading. */
  metaLoading: boolean;
}

interface ExecutionPortfolioCardsProps {
  clientId: string;
  portfolioIds: string[];
  finalistOrder: string[];
  onOpen: (id: string) => void;
}

type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

function MetaSkeleton({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-block align-middle rounded bg-[#e8edf3] animate-pulse ${className}`}
      aria-hidden="true"
    />
  );
}

function cardFromPlaybook(
  id: string,
  playbook: Awaited<ReturnType<typeof fetchExecutionPlaybooks>>['data'][number] | undefined,
  postureLabel: string | null,
): PortfolioCardModel {
  return {
    id,
    title: playbook?.title || playbook?.name || `Initiative ${id}`,
    status: 'Approval Pending',
    sponsor: 'Not assigned',
    owner: 'Not assigned',
    // Prefer Evaluate shortlist posture so Monitor/Scale cannot drift (DMD-1201).
    phase:
      postureLabel
      || formatExecutionPostureLabel(playbook?.complexity)
      || playbook?.complexity
      || 'Planning',
    roi: formatEstimatedValueRangeM(
      playbook?.estimated_value_low_millions,
      playbook?.estimated_value_high_millions,
      playbook?.estimated_value_display,
    ),
    approval: approvalStepsFromProgress(0),
    milestonesDone: 0,
    milestonesTotal: 0,
    nextPhase: '—',
    metaLoading: true,
  };
}

function enrichCardFromViewModel(
  card: PortfolioCardModel,
  viewModel: ReturnType<typeof mapCommandCenter>,
  shortlistPhase: string | null,
): PortfolioCardModel {
  const milestones = viewModel.milestones ?? [];
  const nextMilestone = milestones.find((milestone) => milestone.status !== 'Complete');
  return {
    ...card,
    title: viewModel.header.title || card.title,
    status: viewModel.header.status,
    sponsor: viewModel.header.sponsor,
    owner: viewModel.header.owner,
    phase:
      shortlistPhase
      || formatExecutionPostureLabel(viewModel.header.phase)
      || viewModel.header.phase
      || card.phase,
    roi: viewModel.header.roiRange || card.roi,
    approval: approvalStepsFromProgress(viewModel.approvalSignoff?.completedCount ?? 0),
    milestonesDone:
      viewModel.milestoneSummary.complete
      ?? milestones.filter((m) => m.status === 'Complete').length,
    milestonesTotal: viewModel.milestoneSummary.total ?? milestones.length,
    nextPhase: nextMilestone?.phase || nextMilestone?.title || '—',
    metaLoading: false,
  };
}

export const ExecutionPortfolioCards: React.FC<ExecutionPortfolioCardsProps> = ({
  clientId,
  portfolioIds,
  finalistOrder,
  onOpen,
}) => {
  const { initiativeShortlistBuckets } = useEA();
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cards, setCards] = useState<PortfolioCardModel[]>([]);

  const portfolioKey = useMemo(() => portfolioIds.join(','), [portfolioIds]);

  const postureById = useMemo(() => {
    const map = new Map<string, string>();
    portfolioIds.forEach((id) => {
      const label = shortlistPostureLabel(id, initiativeShortlistBuckets);
      if (label) map.set(String(id), label);
    });
    return map;
  }, [portfolioIds, initiativeShortlistBuckets]);

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    if (portfolioIds.length === 0) {
      setCards([]);
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMessage(null);

    try {
      const playbookById = new Map<string, Awaited<ReturnType<typeof fetchExecutionPlaybooks>>['data'][number]>();
      try {
        const playbooksResult = await fetchExecutionPlaybooks(clientId, portfolioIds);
        playbooksResult.data.forEach((item) => playbookById.set(String(item.use_case_id), item));
      } catch {
        // Cards can still render from IDs; command-center enrich may fill details.
      }
      if (signal?.cancelled) return;

      // Paint the full list immediately — don't block on Command Center.
      const initialCards = portfolioIds.map((id) =>
        cardFromPlaybook(id, playbookById.get(id), postureById.get(String(id)) ?? null),
      );
      setCards(initialCards);
      setStatus('success');

      // Enrich sponsor / owner / approval / milestones in parallel (not 1-by-1).
      const results = await Promise.allSettled(
        portfolioIds.map(async (id) => {
          const viewModel = mapCommandCenter(await fetchCommandCenter(clientId, id));
          return { id, viewModel };
        }),
      );
      if (signal?.cancelled) return;

      const enrichById = new Map<string, ReturnType<typeof mapCommandCenter>>();
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          enrichById.set(result.value.id, result.value.viewModel);
        }
      });

      setCards((prev) =>
        prev.map((card) => {
          const viewModel = enrichById.get(card.id);
          if (!viewModel) {
            return { ...card, metaLoading: false };
          }
          return enrichCardFromViewModel(
            card,
            viewModel,
            postureById.get(String(card.id)) ?? null,
          );
        }),
      );
    } catch (err) {
      if (signal?.cancelled) return;
      setErrorMessage(
        err instanceof ExecutionPlaybooksError || err instanceof CommandCenterError
          ? err.message
          : 'Failed to load the execution portfolio.',
      );
      setCards([]);
      setStatus('error');
    }
  }, [clientId, portfolioIds, postureById]);

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => { signal.cancelled = true; };
  }, [load, portfolioKey]);

  const orderedCards = useMemo(() => {
    const orderMap = new Map(finalistOrder.map((id, index) => [id, index]));
    return [...cards].sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999));
  }, [cards, finalistOrder]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#18365E]/30 border-t-[#18365E] animate-spin mx-auto mb-3" />
        <p className="text-sm font-semibold text-[#18365E]">Loading initiatives…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-white rounded-xl border border-rose-200 px-6 py-10 text-center">
        <p className="text-sm font-bold text-[#18365E]">Unable to load initiatives</p>
        <p className="text-xs text-[#717182] mt-1">{errorMessage}</p>
        <button
          type="button"
          onClick={() => load()}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#18365E] px-3 py-2 rounded-lg"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      {orderedCards.map((card) => {
        const gate = card.approval.find((step) => step.state === 'current');
        return (
          <div
            key={card.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(card.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(card.id);
              }
            }}
            className="bg-white rounded-xl border border-[#e5e7eb] p-5 cursor-pointer hover:border-[#18365E]/40 hover:shadow-sm transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6E4C]/50"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2.5 flex-wrap mb-1">
                  <h3 className="text-sm font-bold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">
                    {card.title}
                  </h3>
                  {card.metaLoading ? (
                    <MetaSkeleton className="h-5 w-14" />
                  ) : (
                    <ExecStatusPill status={card.status} />
                  )}
                </div>

                {card.metaLoading ? (
                  <p className="text-[11px] text-[#717182] flex items-center gap-1.5 flex-wrap">
                    <MetaSkeleton className="h-3 w-28" />
                    <span aria-hidden="true">·</span>
                    <MetaSkeleton className="h-3 w-24" />
                    <span aria-hidden="true">·</span>
                    <span>{card.phase}</span>
                  </p>
                ) : (
                  <p className="text-[11px] text-[#717182]">
                    Sponsor: <b className="text-[#4b5563]">{card.sponsor}</b> · Owner:{' '}
                    <b className="text-[#4b5563]">{card.owner}</b> · {card.phase}
                  </p>
                )}

                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {card.metaLoading ? (
                    <>
                      <MetaSkeleton className="h-2.5 w-28" />
                      <MetaSkeleton className="h-3 w-36" />
                    </>
                  ) : (
                    <>
                      <ApprovalChain steps={card.approval} compact />
                      <span className="text-[10px] text-[#717182]">
                        {gate ? (
                          <span className="font-semibold text-[#FF6E4C]">{gate.label} — in progress</span>
                        ) : card.approval.every((step) => step.state === 'done') ? (
                          'Fully approved'
                        ) : (
                          'Approval not started'
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0">
                <span className="text-[10px] text-[#717182] block">Annual ROI</span>
                <span className="text-base font-bold text-[#18365E]">{card.roi}</span>
                <span className="text-[10px] text-[#717182] block mt-1 min-h-[1rem]">
                  {card.metaLoading ? (
                    <MetaSkeleton className="h-3 w-40 ml-auto" />
                  ) : card.milestonesTotal > 0 ? (
                    `${card.milestonesDone}/${card.milestonesTotal} milestones · next: ${card.nextPhase}`
                  ) : (
                    'Milestones in command center'
                  )}
                </span>
              </div>

              <span className="shrink-0 self-center inline-flex items-center gap-1 text-xs font-semibold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">
                Command center
                <ChevronRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExecutionPortfolioCards;
